import { useState } from 'react';
import { useRouter } from 'next/router';
import CompanyShippingData from '@/assets/img/company-shipping-data.svg';
import ConfigureCompany from '@/assets/img/configure-company.svg';
import MapAddress from '@/assets/img/map-undraw.svg';
import NotifyImage from '@/assets/img/notify-img.svg';
import ModalDefault from '@/components/Modals/ModalDefault';
import ModalEditCompanyInfo from '@/components/Modals/MyCompany/ModalEditCompanyInfo';
import RequestAccess from '@/components/Modals/UserGestation/RequestAccess';
import StepByStepCard from '@/components/StepByStepCard';
import { useUser } from '@/contexts/AuthContext';
import { useRegistrationFlow, QUERIES } from '@/hooks/useRegistrationStep';
import { ROUTER_PAGES } from '@/utils/constants';
import { Typography, Box } from '@mui/material';

enum EditCompanyDataModals {
  CLOSED,
  RESET_COMPANY,
  RESET_COMPANY_ADDRESS,
  RESET_COMPANY_SHIPPING_DATA,
}

interface IEditCompanyDataProps {
  returnToModalHome: () => void;
  onClose: () => void;
}

const EditCompanyData = (props: IEditCompanyDataProps) => {
  const router = useRouter();
  const { returnToModalHome, onClose } = props;
  const { user, defaultMembership } = useUser();
  const { hasRequiredSteps } = useRegistrationFlow(user, defaultMembership);
  const [openShouldInviteAdmin, setOpenShouldInviteAdmin] = useState(false);
  const [openRequestAccess, setOpenRequestAccess] = useState(false);
  const [openShouldValidateEmail, setOpenShouldValidateEmail] = useState(false);
  const [openShouldValidateCpf, setOpenShouldValidateCpf] = useState(false);
  const [currentModal, setCurrentModal] = useState(
    EditCompanyDataModals.CLOSED
  );

  const handleCloseModal = () => setCurrentModal(EditCompanyDataModals.CLOSED);

  const isAdminOfCompany = !!defaultMembership?.is_superadmin;
  const isLastUserConfiguredCompany =
    defaultMembership?.has_confirmed_configuration;

  const canEditCompanyData =
    isAdminOfCompany ||
    (isLastUserConfiguredCompany && defaultMembership?.is_validated);

  const editingCompanyData =
    defaultMembership?.company.has_confirmed_configuration;

  const editingCompanyAddress =
    defaultMembership?.company.has_confirmed_address;

  const editingCompanyShipping =
    defaultMembership?.company.has_confirmed_shipping;

  const handleClickCompanyData = () => {
    if (!userIsValidated()) return;
    if (editingCompanyData) {
      handleEditCompany(EditCompanyDataModals.RESET_COMPANY);
      return;
    }
    setCurrentModal(EditCompanyDataModals.RESET_COMPANY);
  };

  const handleClickCompanyAddress = () => {
    if (!userIsValidated()) return;
    if (editingCompanyAddress) {
      handleEditCompany(EditCompanyDataModals.RESET_COMPANY_ADDRESS);
      return;
    }
    setCurrentModal(EditCompanyDataModals.RESET_COMPANY_ADDRESS);
  };

  const handleClickCompanyShipping = () => {
    if (!userIsValidated()) return;
    if (editingCompanyShipping) {
      handleEditCompany(EditCompanyDataModals.RESET_COMPANY_SHIPPING_DATA);
      return;
    }
    setCurrentModal(EditCompanyDataModals.RESET_COMPANY_SHIPPING_DATA);
  };

  function handleEditCompany(nextStep: EditCompanyDataModals) {
    if (!defaultMembership?.company.has_superadmins && !canEditCompanyData) {
      setOpenShouldInviteAdmin(true);
      return;
    }
    if (!canEditCompanyData) {
      setOpenRequestAccess(true);
      return;
    }
    setCurrentModal(nextStep);
  }

  function userIsValidated() {
    if (hasRequiredSteps === 'VALIDATE_EMAIL') {
      setOpenShouldValidateEmail(true);
      return false;
    }
    if (hasRequiredSteps === 'VALIDATE_CPF') {
      setOpenShouldValidateCpf(true);
      return false;
    }

    return true;
  }

  function redirectNextStep(query: keyof typeof QUERIES) {
    router
      .push({
        pathname: ROUTER_PAGES.home,
        query: { [query]: true },
      })
      .finally(() => {
        returnToModalHome();
        onClose();
      });
  }

  return (
    <>
      <Typography component="h3" variant="h6">
        Editar dados da empresa
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '0 25px',
        }}
      >
        <StepByStepCard
          imageProps={{
            src: ConfigureCompany,
            alt: 'Ilustração do card de alterar os dados da empresa',
          }}
          title={
            editingCompanyData
              ? 'Alterar dados da empresa'
              : 'Configure sua empresa'
          }
          onClick={handleClickCompanyData}
          subtitle={
            editingCompanyData
              ? defaultMembership?.company.full_name ||
                defaultMembership?.company.short_name
              : 'Informe alguns dados da empresa que você representa com os detalhes de sua atuação.'
          }
          data-testid="card-company-data"
        />

        {canEditCompanyData && (
          <StepByStepCard
            imageProps={{
              src: MapAddress,
              alt: 'Imagem de um mapa ilustrativo no card de confirmar o endereço',
            }}
            title={
              editingCompanyAddress ? 'Editar endereço' : 'Confirmar endereço'
            }
            subtitle={
              editingCompanyAddress
                ? 'Edite o endereço da sua empresa.'
                : 'Confirme o endereço da sua empresa.'
            }
            onClick={handleClickCompanyAddress}
            disabled={!defaultMembership?.company.has_confirmed_configuration}
            data-testid="card-company-address"
          />
        )}

        <StepByStepCard
          imageProps={{
            src: CompanyShippingData,
            alt: 'Imagem de um mapa ilustrativo no card de alterar os dados de entrega e pagamento',
          }}
          title="Entrega e pagamento"
          subtitle={
            editingCompanyShipping
              ? 'Edite os detalhes de entrega e pagamento da sua empresa.'
              : 'Configure os detalhes de entrega e pagamento da sua empresa.'
          }
          onClick={handleClickCompanyShipping}
          disabled={!defaultMembership?.company.has_confirmed_configuration}
          data-testid="card-company-shipping-data"
        />
      </Box>

      <ModalEditCompanyInfo
        currentModal={currentModal}
        setCurrentModal={setCurrentModal}
        handleClose={handleCloseModal}
      />

      <RequestAccess
        isOpen={openRequestAccess}
        handleClose={() => setOpenRequestAccess(false)}
      />

      <ModalDefault
        isOpen={openShouldValidateEmail}
        onClose={() => setOpenShouldValidateEmail(false)}
        src={NotifyImage}
        alt="Imagem ilustrando um aviso"
        title="Atenção fornecedor!"
        subtitle="Você precisa validar seu e-mail primeiro!"
        textButton="Validar email"
        onClickButton={() => redirectNextStep('verified_email')}
      />

      <ModalDefault
        isOpen={openShouldValidateCpf}
        onClose={() => setOpenShouldValidateCpf(false)}
        src={NotifyImage}
        alt="Imagem ilustrando um aviso"
        title="Atenção fornecedor!"
        subtitle="Você precisa validar seu CPF primeiro!"
        textButton="Validar cpf"
        onClickButton={() => redirectNextStep('validate_cpf')}
      />

      <ModalDefault
        isOpen={openShouldInviteAdmin}
        onClose={() => setOpenShouldInviteAdmin(false)}
        src={NotifyImage}
        alt="Imagem ilustrando um aviso"
        title="Atenção fornecedor!"
        subtitle="Esta empresa ainda não possui um administrador. Defina um administrador ou envie um convite por e-mail."
        textButton="Definir administrador"
        onClickButton={() => {
          setOpenShouldInviteAdmin(false);
          redirectNextStep('invite_admin');
        }}
      />
    </>
  );
};

export default EditCompanyData;
export { EditCompanyDataModals };
export type { IEditCompanyDataProps };
